import { ColumnDef } from '@tanstack/react-table'
import { Automation } from '@/interfaces/automation'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTableColumnHeader } from './data-table-column-header'
import { DataTableRowActions } from './data-table-row-actions'

const triggerTypeLabels: Record<Automation['triggerType'], string> = {
  KEYWORD: 'Palavra-chave',
  SCHEDULE: 'Agendamento',
  EVENT: 'Webhook',
  CONDITION: 'Segmento',
}

const actionTypeLabels: Record<Automation['actionType'], string> = {
  ROUTING: 'Fluxo',
  AGENT: 'Agente IA',
}

export const columns: ColumnDef<Automation>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label='Select all'
        className='translate-y-[2px]'
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label='Select row'
        className='translate-y-[2px]'
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Nome' />
    ),
    cell: ({ row }) => {
      return (
        <div className='flex space-x-2'>
          <span className='max-w-32 truncate font-medium sm:max-w-72 md:max-w-[31rem]'>
            {row.getValue('name')}
          </span>
        </div>
      )
    },
  },
  {
    accessorKey: 'triggerType',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Trigger' />
    ),
    cell: ({ row }) => {
      const triggerType = row.getValue<Automation['triggerType']>('triggerType')
      return (
        <div className='flex items-center'>
          <Badge variant='outline'>{triggerTypeLabels[triggerType]}</Badge>
        </div>
      )
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id))
    },
  },
  {
    accessorKey: 'actionType',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Ação' />
    ),
    cell: ({ row }) => {
      const actionType = row.getValue<Automation['actionType']>('actionType')
      return (
        <div className='flex items-center'>
          <Badge variant='secondary'>{actionTypeLabels[actionType]}</Badge>
        </div>
      )
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id))
    },
  },
  {
    accessorKey: 'isActive',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Status' />
    ),
    cell: ({ row }) => {
      const isActive = row.getValue<boolean>('isActive')
      return (
        <div className='flex items-center'>
          <Badge variant={isActive ? 'default' : 'destructive'}>
            {isActive ? 'Ativo' : 'Inativo'}
          </Badge>
        </div>
      )
    },
    filterFn: (row, id, value) => {
      const isActive = row.getValue<boolean>(id)
      return value.includes(isActive ? 'true' : 'false')
    },
  },
  {
    accessorKey: 'stats',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Execuções' />
    ),
    cell: ({ row }) => {
      const stats = row.getValue<Automation['stats']>('stats')
      return (
        <div className='flex items-center'>
          <span className='text-sm text-muted-foreground'>
            {stats.executionCount}
          </span>
        </div>
      )
    },
    enableSorting: false,
  },
  {
    id: 'actions',
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
]
